import { DATE_TIME_FORMAT } from "@/v2/assets/utils";
import dayjs from "dayjs";

type MinutePreset = `${number}m`;
type HourPreset = `${number}h`;
type DayPreset = `${number}d`;
type WeekPreset = `${number}w`;
type MonthPreset = `${number}mo`;

export type PresetValue =
  | MinutePreset
  | HourPreset
  | DayPreset
  | WeekPreset
  | MonthPreset
  | "today"
  | "yesterday"
  | "live";

export function isPresetValue(input: unknown): input is PresetValue {
  if (typeof input !== "string") return false;

  // fixed presets
  if (input === "today" || input === "yesterday" || input === "live") {
    return true;
  }

  // pattern presets
  const presetRegex = /^(\d+)(m|h|d|w|mo)$/;

  return presetRegex.test(input);
}

export type DateRange =
  | { type: "custom"; dateFrom: string; dateTo: string }
  | { type: "preset"; value: PresetValue };

function parsePresetDateRange(preset: PresetValue) {
  const now = dayjs();
  const dateTo = now.toISOString();

  // LIVE → 15 minutes ago to now
  if (preset === "live") {
    return {
      dateFrom: now.subtract(15, "minute").toISOString(),
      dateTo,
    };
  }

  // TODAY → midnight to now
  if (preset === "today") {
    return {
      dateFrom: now.startOf("day").toISOString(),
      dateTo,
    };
  }

  // YESTERDAY → 00:00 to 23:59:59.999
  if (preset === "yesterday") {
    const y = now.subtract(1, "day");
    return {
      dateFrom: y.startOf("day").toISOString(),
      dateTo: y.endOf("day").toISOString(),
    };
  }

  // Handle numeric presets: "15m", "1h", "7d", "2w", "3mo"
  const unitMatch = preset.match(/^(\d+)(m|h|d|w|mo)$/);
  if (!unitMatch) {
    throw new Error(`Invalid preset format: ${preset}`);
  }
  const [, amountStr, unit] = unitMatch;
  const amount = Number(amountStr);

  if (isNaN(amount)) {
    throw new Error(`Invalid preset: ${preset}`);
  }

  let dateFrom = now;

  switch (unit) {
    case "m":
      dateFrom = now.subtract(amount, "minute");
      break;

    case "h":
      dateFrom = now.subtract(amount, "hour");
      break;

    case "d":
      dateFrom = now.subtract(amount, "day");
      break;

    case "w":
      dateFrom = now.subtract(amount, "week");
      break;

    case "mo":
      dateFrom = now.subtract(amount, "month");
      break;

    default:
      throw new Error(`Unknown unit in preset: ${preset}`);
  }

  return {
    dateFrom: dateFrom.toISOString(),
    dateTo,
  };
}

export function parseDateRange(dateRange: DateRange) {
  if (dateRange.type === "custom") {
    return {
      dateFrom: dateRange.dateFrom,
      dateTo: dateRange.dateTo,
    };
  }

  return parsePresetDateRange(dateRange.value);
}

export function formatPresetValue(preset: PresetValue) {
  if (preset === "live") {
    return "15 Minutes";
  }

  if (preset === "today") {
    return "Today";
  }

  if (preset === "yesterday") {
    return "Yesterday";
  }

  const presetPattern = /^(\d+)(m|h|d|w|mo)$/;
  const match = preset.match(presetPattern);

  if (match) {
    const amount = Number(match[1]);
    const unit = match[2];
    let unitLabel = "";
    switch (unit) {
      case "m":
        unitLabel = "Minute";
        break;
      case "h":
        unitLabel = "Hour";
        break;
      case "d":
        unitLabel = "Day";
        break;
      case "w":
        unitLabel = "Week";
        break;
      case "mo":
        unitLabel = "Month";
        break;
      default:
        unitLabel = unit;
    }
    return `Past ${amount} ${unitLabel}${amount !== 1 ? "s" : ""}`;
  }

  return preset;
}

export function formatDateRange(dateRange: DateRange) {
  if (dateRange.type === "custom") {
    return `${dayjs(dateRange.dateFrom).format(DATE_TIME_FORMAT)} - ${dayjs(
      dateRange.dateTo
    ).format(DATE_TIME_FORMAT)}`;
  }

  return formatPresetValue(dateRange.value);
}
